import React from 'react';
import type { CityOperationalStats } from '../../services/xtamDashboardsApi';
import './CityStatusCard.css';

type Props = {
  cityLabel: string;
  online: boolean;
  stats: CityOperationalStats;
  lastUpdated: string | null;
  loading?: boolean;
  errorMessage?: string | null;
  onRetry?: () => void;
};

function pctColor(pct: number): string {
  if (pct >= 90) return 'xtd-city-gauge__pct--ok';
  if (pct >= 70) return 'xtd-city-gauge__pct--warn';
  return 'xtd-city-gauge__pct--bad';
}

function Gauge({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  const angle = (clamped / 100) * 180;
  const rad = ((180 - angle) * Math.PI) / 180;
  const cx = 50;
  const cy = 52;
  const r = 38;
  const nx = cx + r * Math.cos(rad);
  const ny = cy - r * Math.sin(rad);
  const large = angle > 90 ? 1 : 0;
  const endX = cx + r * Math.cos(Math.PI);
  const endY = cy - r * Math.sin(Math.PI);

  return (
    <svg viewBox="0 0 100 58" width="100" height="58" aria-hidden className="xtd-city-gauge__svg">
      <path
        d="M 12 52 A 38 38 0 0 1 88 52"
        fill="none"
        stroke="#374151"
        strokeWidth="8"
        strokeLinecap="round"
      />
      <path
        d={`M 12 52 A 38 38 0 ${large} 1 ${endX} ${endY}`}
        fill="none"
        stroke="#22c55e"
        strokeWidth="8"
        strokeLinecap="round"
        opacity={0.35}
      />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#e5e7eb" strokeWidth="2" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="3" fill="#e5e7eb" />
    </svg>
  );
}

function OnOffRows({ on, off }: { on: number; off: number }) {
  return (
    <div className="xtd-onoff-list">
      <div className="xtd-onoff-row">
        <span className="xtd-onoff-dot xtd-onoff-dot--on" aria-hidden />
        <span className="xtd-onoff-label">ON</span>
        <span className="xtd-onoff-value">{on.toLocaleString('es-CO')}</span>
      </div>
      <div className="xtd-onoff-row">
        <span className="xtd-onoff-dot xtd-onoff-dot--off" aria-hidden />
        <span className="xtd-onoff-label">OFF</span>
        <span className="xtd-onoff-value">{off.toLocaleString('es-CO')}</span>
      </div>
    </div>
  );
}

function StatColumn({
  heading,
  total,
  pctLabel,
  on,
  off,
}: {
  heading: string;
  total: number;
  pctLabel: string;
  on: number;
  off: number;
}) {
  return (
    <div className="xtd-stat-block">
      <div className="xtd-stat-block__total">{total.toLocaleString('es-CO')}</div>
      <div className="xtd-stat-block__heading">{heading}</div>
      <div className="xtd-stat-block__pct">{pctLabel}</div>
      <OnOffRows on={on} off={off} />
    </div>
  );
}

const CityStatusCard: React.FC<Props> = ({
  cityLabel,
  online,
  stats,
  lastUpdated,
  loading,
  errorMessage,
  onRetry,
}) => {
  const gaugePct = stats.sitesTotal > 0 ? stats.sitesPct : 0;

  return (
    <article className="xtd-city-card" aria-busy={loading}>
      <header className="xtd-city-card__head">
        <h3 className="xtd-city-card__title" title={cityLabel}>
          {cityLabel}
        </h3>
        <span
          className={`xtd-city-card__badge ${online ? 'xtd-city-card__badge--on' : 'xtd-city-card__badge--off'}`}
        >
          {online ? 'ONLINE' : 'OFFLINE'}
        </span>
      </header>

      <div className="xtd-city-card__body">
        <div className="xtd-city-gauge">
          <Gauge pct={gaugePct} />
          <div className={`xtd-city-gauge__pct ${pctColor(gaugePct)}`}>{gaugePct.toFixed(1)}%</div>
          <div className="xtd-city-gauge__sub">
            {stats.sitesOn.toLocaleString('es-CO')} / {stats.sitesTotal.toLocaleString('es-CO')} sitios
          </div>
        </div>

        <div className="xtd-city-stats">
          <StatColumn
            heading="Sitios"
            total={stats.sitesTotal}
            pctLabel={`SIT. ${stats.sitesPct.toFixed(1)}%`}
            on={stats.sitesOn}
            off={stats.sitesOff}
          />
          <StatColumn
            heading="Cámaras"
            total={stats.camsTotal}
            pctLabel={`CÁM. ${stats.camsPct.toFixed(1)}%`}
            on={stats.camsOn}
            off={stats.camsOff}
          />
        </div>
      </div>

      {!online && (
        <footer className="xtd-city-card__foot">
          {errorMessage || 'Sin conexión con la ciudad.'}
          {lastUpdated ? ` Último dato: ${lastUpdated}.` : ' Último dato: —.'}
          {' · reintentando cada 60 s'}
          {onRetry && (
            <button type="button" className="xtd-city-card__retry" onClick={onRetry} disabled={loading}>
              Reintentar
            </button>
          )}
        </footer>
      )}
    </article>
  );
};

export default CityStatusCard;
